import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Card,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton,
} from '@mui/material';
import { 
  generateImage, 
  getHistory, 
  attachImageToArticle, 
  attachImageToParagraph, 
  uploadImage,
  getImageModels,
  getProjectSettings,
  generateImagePrompt
} from '../services/api';
import { useSnackbar } from 'notistack';
import UploadIcon from '@mui/icons-material/Upload';
import CropLandscapeIcon from '@mui/icons-material/CropLandscape';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CropFreeIcon from '@mui/icons-material/CropFree';
import CropRotateIcon from '@mui/icons-material/CropRotate';
import ImageLibrary from './image_dialog/ImageLibrary';
import StockImageSearch from './image_dialog/StockImageSearch';
import { LazyImage } from './image_dialog/LazyImage';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import { useMediaQuery } from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';

const ImageGeneratorDialog = ({ 
  open, 
  onClose, 
  articleId, 
  paragraphId = null, 
  paragraphContent = null,
  paragraphHeading = null,
  initialPrompt = '',
  onImageGenerated,
  contentOnly = false,
  projectId
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [recentImages, setRecentImages] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [selectedModel, setSelectedModel] = useState('');
  const [dimensionPreset, setDimensionPreset] = useState('landscape');
  const [models, setModels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [imageLibrary, setImageLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [libraryParams, setLibraryParams] = useState({
    page: 1,
    per_page: 100,
    used_only: false,
    search: '',
    sort_by: 'date',
    sort_order: 'desc'
  });
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = useRef(null);
  const [generatingPrompt, setGeneratingPrompt] = useState(false);

  // Get current model details
  const currentModel = models.find(m => m.name === selectedModel);

  // Load project settings when dialog opens
  useEffect(() => {
    const loadProjectSettings = async () => {
      if (!projectId) return;
      try {
        const settings = await getProjectSettings(projectId);
        setSettings(settings);
        if (settings.image_provider) {
          setSelectedModel(settings.image_provider);
        }
      } catch (err) {
        console.error('Error loading project settings:', err);
      }
    };

    if (open) {
      loadProjectSettings();
    }
  }, [open, projectId]);

  // Fetch available models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const data = await getImageModels();
        setModels(data);
        if (data.length > 0 && !selectedModel) {
          setSelectedModel(data[0].name);
        }
      } catch (err) {
        setError('Error loading models: ' + err.message);
      }
    };

    if (open) {
      fetchModels();
    }
  }, [open]);

  useEffect(() => {
    if (open && tabValue === 1) {
      loadImageLibrary();
    }
  }, [open, tabValue, libraryParams]);

  const loadImageLibrary = async () => {
    if (!projectId) return;
    
    try {
      setLibraryLoading(true);
      const data = await getHistory({
        ...libraryParams,
        project_id: projectId
      });
      setImageLibrary(data.items);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError('Error loading image library');
    } finally {
      setLibraryLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentModelDetails = models.find(m => m.name === selectedModel);
      const result = await generateImage({
        prompt,
        model: selectedModel,
        dimension_preset: dimensionPreset,
        project_id: projectId,
      });
      
      // Take the first generated image for now
      const newImage = {
        ...result[0],
        source: selectedModel
      };
      setGeneratedImage(newImage);
      
      // Update recent images
      setRecentImages(prev => [newImage, ...prev].slice(0, 4));

      // Show credit usage notification
      const creditsUsed = currentModelDetails.price_per_image;
      enqueueSnackbar(`-${creditsUsed.toFixed(2)} credits used`, { 
        variant: 'info',
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left'
        }
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      enqueueSnackbar(err.response?.data?.detail || err.message, { 
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyGenerated = async () => {
    if (!generatedImage) return;
    
    try {
      setLoading(true);
      setError(null);
      
      if (!contentOnly) {
        if (paragraphId) {
          await attachImageToParagraph(articleId, paragraphId, { id: generatedImage.id });
        } else {
          await attachImageToArticle(articleId, { id: generatedImage.id });
        }
      }

      if (onImageGenerated) {
        onImageGenerated(generatedImage);
      }
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectImage = async (image) => {
    try {
      setLoading(true);
      setError(null);

      if (!contentOnly) {
        if (paragraphId) {
          await attachImageToParagraph(articleId, paragraphId, { id: image.id });
        } else {
          await attachImageToArticle(articleId, { id: image.id });
        }
      }

      if (onImageGenerated) {
        onImageGenerated(image);
      }
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadFile(file);
      const previewUrl = URL.createObjectURL(file);
      setUploadPreview(previewUrl);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    
    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const uploadResult = await uploadImage(formData, projectId);
      
      if (paragraphId) {
        await attachImageToParagraph(articleId, paragraphId, { id: uploadResult.id });
      } else {
        await attachImageToArticle(articleId, { id: uploadResult.id });
      }

      if (onImageGenerated) {
        onImageGenerated(uploadResult);
      }
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReusePrompt = (prompt) => {
    setPrompt(prompt);
    setTabValue(0); // Switch to Generate New tab
  };

  const handleClose = () => {
    setPrompt(initialPrompt);
    setGeneratedImage(null);
    setRecentImages([]);
    setError(null);
    setTabValue(0);
    setUploadFile(null);
    setUploadPreview(null);
    setDimensionPreset('landscape');
    onClose();
  };

  const handleGeneratePrompt = async () => {
    if (!paragraphContent || !projectId) return;
    
    try {
      setGeneratingPrompt(true);
      setError(null);
      
      const data = await generateImagePrompt(paragraphContent, paragraphHeading, projectId);
      setPrompt(data.prompt);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      setError('Error generating prompt: ' + errorMessage);
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left'
        }
      });
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const dimensionPresetIcons = {
    square: <CropSquareIcon />,
    portrait: <CropPortraitIcon />,
    landscape: <CropLandscapeIcon />,
    wide: <CropFreeIcon />,
    story: <CropRotateIcon />
  };

  // Debug log to check content
  useEffect(() => {
    console.log('Paragraph content:', paragraphContent);
    console.log('Paragraph heading:', paragraphHeading);
  }, [paragraphContent, paragraphHeading]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={useMediaQuery((theme) => theme.breakpoints.down('sm'))}
      sx={{
        '& .MuiDialog-paper': {
          height: { xs: '100%', sm: 'auto' }
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        bgcolor: 'background.paper',
        zIndex: 1
      }}>
        <Typography variant="h6" component="div">
          {projectId ? (
            <>Generate Image {paragraphId ? 'for Paragraph' : 'for Article'} - {projectId}</>
          ) : (
            'Generate Image'
          )}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          sx={{ display: { sm: 'none' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {!projectId ? (
        <DialogContent>
          <Typography color="error" sx={{ mb: 2 }}>
            Please select a project first to manage images
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Images are organized by project. You need to select a project from the top navigation bar to proceed.
          </Typography>
        </DialogContent>
      ) : (
        <>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              minHeight: { xs: 48, sm: 'auto' },
              '& .MuiTab-root': {
                minHeight: { xs: 48, sm: 'auto' },
                py: { xs: 1, sm: 2 },
                px: { xs: 2, sm: 3 }
              }
            }}
          >
            <Tab 
              label={
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}>
                  <AddPhotoAlternateIcon sx={{ display: { xs: 'block', sm: 'none' } }} />
                  <span sx={{ display: { xs: 'none', sm: 'block' } }}>Generate New</span>
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}>
                  <CollectionsIcon sx={{ display: { xs: 'block', sm: 'none' } }} />
                  <span sx={{ display: { xs: 'none', sm: 'block' } }}>Library</span>
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}>
                  <UploadIcon sx={{ display: { xs: 'block', sm: 'none' } }} />
                  <span sx={{ display: { xs: 'none', sm: 'block' } }}>Upload</span>
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}>
                  <ImageSearchIcon sx={{ display: { xs: 'block', sm: 'none' } }} />
                  <span sx={{ display: { xs: 'none', sm: 'block' } }}>Stock</span>
                </Box>
              }
            />
          </Tabs>
          <DialogContent sx={{ 
            p: { xs: 1, sm: 3 },
            height: { xs: 'calc(100vh - 120px)', sm: 'auto' },
            overflow: 'auto'
          }}>
            {tabValue === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2, 
                mt: 1,
                pb: 2
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 2, 
                  mb: 2 
                }}>
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <FormControl fullWidth>
                      <InputLabel id="model-select-label">Model</InputLabel>
                      <Select
                        labelId="model-select-label"
                        value={selectedModel}
                        label="Model"
                        onChange={(e) => setSelectedModel(e.target.value)}
                        disabled={loading}
                      >
                        {models.map(model => (
                          <MenuItem key={model.name} value={model.name}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>{model.name}</Typography>
                                {model.name === settings?.image_provider && (
                                  <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                                    (Default)
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {model.description} - {model.price_per_image} credits/image
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Tooltip title="Set default model in project settings">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/settings?highlight=image_provider`)}
                        sx={{ color: 'primary.main' }}
                      >
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ 
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    justifyContent: 'space-between'
                  }}>
                    <Button
                      onClick={handleGeneratePrompt}
                      disabled={!paragraphContent || loading || generatingPrompt}
                      variant="outlined"
                      size="small"
                      color="primary"
                      sx={{ 
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flex: { xs: 1, sm: 'none' }
                      }}
                    >
                      {generatingPrompt ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <>
                          <AutoFixHighIcon fontSize="small" />
                          Suggest
                        </>
                      )}
                    </Button>

                    {currentModel && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: 'flex-end',
                        flex: { xs: 1, sm: 'none' }
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          whiteSpace: 'nowrap',
                          display: { xs: 'none', sm: 'block' }
                        }}>
                          Dimensions:
                        </Typography>
                        <ToggleButtonGroup
                          value={dimensionPreset}
                          exclusive
                          size="small"
                          onChange={(e, newValue) => newValue && setDimensionPreset(newValue)}
                          disabled={loading}
                        >
                          {currentModel.supported_dimensions.map(preset => (
                            <ToggleButton key={preset} value={preset}>
                              <Tooltip title={preset.charAt(0).toUpperCase() + preset.slice(1)}>
                                {dimensionPresetIcons[preset]}
                              </Tooltip>
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                    )}
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Image Prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  disabled={loading || generatingPrompt}
                />

                {error && (
                  <Typography color="error">
                    Error: {error}
                  </Typography>
                )}

                {generatedImage && (
                  <>
                    <Card>
                      <Box sx={{ 
                        width: '100%',
                        minHeight: { xs: '200px', sm: '300px' },
                        maxHeight: { xs: '50vh', sm: '70vh' },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: 'background.default',
                        p: 2,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          position: 'relative'
                        }}>
                          <LazyImage
                            imageUrl={generatedImage.image_url}
                            alt="Generated image"
                            sx={{ 
                              maxWidth: '100%',
                              maxHeight: '100%',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Generated using: {generatedImage.source}
                        </Typography>
                      </Box>
                    </Card>
                    {recentImages.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Recent Generations:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                          {recentImages.map((img, index) => (
                            <Card 
                              key={index} 
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                },
                                flexShrink: 0,
                                width: { xs: 100, sm: 150 },
                                height: { xs: 100, sm: 150 }
                              }}
                              onClick={() => setGeneratedImage(img)}
                            >
                              <Box sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                bgcolor: 'background.default'
                              }}>
                                <LazyImage
                                  imageUrl={img.image_url}
                                  alt={`Recent generation ${index + 1}`}
                                  sx={{ 
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain'
                                  }}
                                />
                              </Box>
                            </Card>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            ) : tabValue === 1 ? (
              <ImageLibrary
                loading={libraryLoading}
                error={error}
                imageLibrary={imageLibrary}
                libraryParams={libraryParams}
                setLibraryParams={setLibraryParams}
                totalPages={totalPages}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                onImageSelect={handleSelectImage}
                disabled={loading}
                onReusePrompt={handleReusePrompt}
              />
            ) : tabValue === 2 ? (
              <Box sx={{ 
                mt: 2, 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2,
                pb: 2
              }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>
                {uploadPreview && (
                  <Card>
                    <Box sx={{ 
                      width: '100%',
                      height: { xs: '300px', sm: '400px' },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      bgcolor: 'background.default'
                    }}>
                      <LazyImage
                        imageUrl={uploadPreview}
                        alt="Upload preview"
                        sx={{ 
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  </Card>
                )}
                {error && (
                  <Typography color="error">
                    Error: {error}
                  </Typography>
                )}
              </Box>
            ) : (
              <StockImageSearch
                onImageSelected={handleSelectImage}
                disabled={loading}
                projectId={projectId}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ 
            borderTop: 1, 
            borderColor: 'divider',
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: 1
          }}>
            <Button onClick={handleClose}>
              Close
            </Button>
            {tabValue === 0 && (
              <>
                <Button 
                  onClick={handleGenerate}
                  variant="contained"
                  disabled={!prompt.trim() || !selectedModel || loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate'}
                </Button>
                {generatedImage && (
                  <Button
                    onClick={handleApplyGenerated}
                    variant="contained"
                    color="success"
                    disabled={loading}
                  >
                    Attach
                  </Button>
                )}
              </>
            )}
            {tabValue === 2 && (
              <Button
                onClick={handleUpload}
                variant="contained"
                disabled={!uploadFile || loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Upload'}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ImageGeneratorDialog;
